import { BidContext } from '@/pages/Home/HomePage';
import useToken from '@/utils/useToken';
import {
  Button,
  Icon,
  LoadingSpinner,
} from '@data-products-and-ai/react-components';
import React, { useEffect, useRef, useState } from 'react';
import zoomPlugin from 'chartjs-plugin-zoom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  zoomPlugin,
);
const color = '#8a00e5';

const WinningGraph: React.FC = () => {
  const selectionBid = React.useContext(BidContext);
  const getToken = useToken();
  const chartRef = useRef<ChartJS<'line', number[], number> | null>(null);

  interface WinningData {
    labels: number[];
    datasets: {
      data: number[];
    }[];
  }
  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Winning Graph',
      },
      tooltip: {
        callbacks: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          label: function (context: any) {
            return (
              'Probability of Winning: ' +
              context.dataset.data[context.parsed.x]
            );
          },
        },
      },
      zoom: {
        pan: {
          enabled: false,
          mode: 'xy' as 'x' | 'y' | 'xy',
        },
        zoom: {
          wheel: {
            enabled: false,
          },
          pinch: {
            enabled: false,
          },
          mode: 'xy' as 'x' | 'y' | 'xy',
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Price Change',
        },
      },
      y: {
        title: {
          display: true,
          text: 'Probability of Winning',
        },
      },
    },
    animation: {
      duration: 1000,
      easing: 'linear' as const,
    },
  };

  const handleZoom = (action: string) => {
    if (chartRef.current) {
      if (action === 'reset') chartRef.current.resetZoom();
      if (action === 'in') chartRef.current.zoom(1.1);
      if (action === 'out') chartRef.current.zoom(0.9);
    }
  };

  const [winningData, setWinningData] = React.useState<WinningData>({
    labels: [],
    datasets: [],
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const token = await getToken();
      fetch(`${import.meta.env.VITE_APP_API_URL}/get_prediction_route`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token.accessToken}`,
        },
        body: JSON.stringify(selectionBid),
      })
        .then((response) => response.json())
        .then((data) => {
          const plotData = data.PriceCurve;
          const parsedData = parseData(plotData);
          setWinningData(parsedData);
          //setpredictionDataTooltip(plotData.data[1].text ?? []);
        })
        .catch((error) => {
          console.log('Error fetching data from the API', error);
        })
        .finally(() => {
          setLoading(false);
        });
    };
    if (!Object.keys(selectionBid).length) return;
    fetchData();
  }, [selectionBid]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const parseData = (plotData: any[]) => {
    const labels = plotData.map((item) => item['Price Change']);
    const data = plotData.map((item) => item['Win Probability']);
    const returnData = {
      labels: labels,
      datasets: [
        {
          label: 'Price Change',
          data: data,
          borderColor: '#8a00e5',
        },
      ],
    };
    return returnData;
  };

  return (
    <div>
      <div className="winningGraph_top-bar">
        <h5>Probability of Winning</h5>
        <div className='winningGraph_top-bar_buttons'>
          <Button
            type="primaryOutline"
            allCaps={false}
            size="small"
            onClick={()=>handleZoom('in')}
            icon={<Icon icon="IconZoomIn" iconSize={0.9} color={color} />}
          >
            Zoom In
          </Button>
          <Button
            type="primaryOutline"
            allCaps={false}
            size="small"
            onClick={()=>handleZoom('out')}
            icon={<Icon icon="IconZoomOut" iconSize={0.9} color={color} />}
          >
            Zoom out
          </Button>
          <Button
            type="primaryOutline"
            allCaps={false}
            size="small"
            onClick={()=>handleZoom('reset')}
            icon={<Icon icon="IconReset" iconSize={0.9} color={color} />}
          >
            Reset
          </Button>
        </div>
      </div>
      {loading ? (
        <LoadingSpinner
          color="#8a00e5"
          backgroundColor="#ADA9C2"
          text="Loading..."
        />
      ) : (
        <Line
          ref={chartRef}
          data={winningData}
          options={options}
          height={'150px'}
        />
      )}
    </div>
  );
};

export default WinningGraph;
